import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core'
import {
  ApolloClientOptions,
  ApolloLink,
  concat,
  InMemoryCache,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular'
import { createUploadLink } from 'apollo-upload-client'
import { SegmentService } from '@wm-front/core'
import { GraphQLModuleOptions } from '@wm-front/shared/interfaces'
import { generateUUID } from '@datadog/browser-core'

export const GRAPHQL_MODULE_OPTIONS = new InjectionToken<GraphQLModuleOptions>(
  'gqlOptions'
)

export const createApolloFactory = (
  graphQLModuleOptions: GraphQLModuleOptions,
  segmentService: SegmentService
): ApolloClientOptions<unknown> => {
  const firstLink = createUploadLink({ uri: graphQLModuleOptions.graphqlUrl })

  const errorHandler = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        )
      )
    }

    if (networkError) console.log(`[Network error]: ${networkError}`)
  })

  /** Set the authentication token in local storage if in queryparams **/
  let token = ''
  let tokenKey = ''

  const urlParams = new URLSearchParams(window.location.search)
  const url = window.location.href
  const urlsWhichShouldIgnoreToken = ['auth/create-password']

  switch (graphQLModuleOptions.appName) {
    case 'wm-front':
      tokenKey = 'wm-client-token'
      break
    case 'wm-admin':
      tokenKey = 'wm-admin-token'
      break
    case 'wm-reports':
      tokenKey = 'wm-reports-token'
      break
  }

  const tokenParam = urlParams.get('token')

  if (
    tokenParam &&
    !urlsWhichShouldIgnoreToken.some((u: string) => url.includes(u))
  ) {
    token = tokenParam
    window.localStorage.setItem(tokenKey, tokenParam)
  }

  const authLink = new ApolloLink((operation, forward) => {
    /** Get the authentication token from local storage if it exists **/
    token = window.localStorage.getItem(tokenKey) as string

    /** The operations that do not require a token */
    const noTokenOperations = [
      'Login',
      'ForgotPassword',
      'UpdatePasswordWithToken',
    ]

    let result = null

    /** Use the setContext method to set the HTTP headers. **/
    const locale = window.localStorage.getItem('wm-locale')

    /** Remove all empty filters given as parameters to the API
     * as it does not support it and returns incoherent results
     */
    /** lines/excludedRequestItems are wholesale on setQuoteLines: an empty
     * array means "none", not "not provided" (PRENG-7516) */
    const exceptions = ['issueIds', 'lines', 'excludedRequestItems']
    for (const variable in operation.variables) {
      for (const filter in operation.variables[variable]) {
        if (
          !exceptions.includes(filter) &&
          operation.variables[variable][filter] instanceof Array &&
          !operation.variables[variable][filter].length
        ) {
          delete operation.variables[variable][filter]
        }
      }
    }

    operation.setContext({
      headers: {
        Authorization:
          token && !noTokenOperations.includes(operation.operationName)
            ? `Bearer ${token}`
            : '',
        [`Accept-Language`]: locale || 'fr-FR',
        'x-request-id': generateUUID(),
      },
    })

    /** Call the next link in the middleware chain. **/
    if (forward) {
      result = forward(operation) as any
    }

    return result
  })

  const analyticsLink = new ApolloLink((operation, forward) => {
    segmentService.trackGraphqlRequest(operation)
    return forward ? forward(operation) : null
  })

  const linksConcat = [firstLink, errorHandler, authLink, analyticsLink].reduce(
    (acc, cur) => {
      return concat(cur as ApolloLink, acc as ApolloLink)
    }
  )

  return {
    link: linksConcat as ApolloLink,
    cache: new InMemoryCache({
      possibleTypes: {
        AuditReportFormField: [
          'AuditReportFormFieldOption',
          'AuditReportFormFieldRadio',
        ],
        Thing: ['ElevatorModuleThing', 'EscalatorModuleThing', 'GenericThing'],
      },
      typePolicies: {
        Inference: {
          // An Inference "id" is the prompt id, shared by every inference of
          // the same prompt (the federated key is "id variables"). Normalizing
          // by id would merge e.g. the digests of every table row into a
          // single cache entry: keep inferences embedded in their parent.
          keyFields: false,
        },
      },
    }),
    connectToDevTools: true,
  }
}

/**
 * Main module to bootstrap and provide Apollo which is used to
 * query/mutate data from and to the backend
 */
@NgModule({
  imports: [ApolloModule],
})
export class GraphQLModule {
  public static forRoot(
    graphQLModuleOptions?: GraphQLModuleOptions
  ): ModuleWithProviders<GraphQLModule> {
    return {
      ngModule: GraphQLModule,
      providers: [
        {
          provide: GRAPHQL_MODULE_OPTIONS,
          useValue: graphQLModuleOptions,
        },
        {
          provide: APOLLO_OPTIONS,
          useFactory: createApolloFactory,
          deps: [GRAPHQL_MODULE_OPTIONS, SegmentService],
        },
      ],
    }
  }
}
